import React, { useState } from "react";
import { useCampaigns, useAccounts, useSortableData } from "../hooks/useAdData";
import { optimizeCampaign } from "../services/api";

export default function Campaigns() {
  const [platform, setPlatform]       = useState("");
  const [dateRange, setDateRange]     = useState("LAST_30_DAYS");
  const [accountId, setAccountId]     = useState("");

  const { accounts } = useAccounts();
  const { campaigns: rawCampaigns, loading, error, lastSynced, sync } = useCampaigns(platform, dateRange, accountId);
  const { sorted: campaigns, sortKey, sortDir, requestSort } = useSortableData(rawCampaigns, "spend", "desc");

  const [optimizing, setOptimizing]     = useState(null);
  const [optimizeResult, setOptimizeResult] = useState(null);

  const handleOptimize = async (campaign) => {
    setOptimizing(campaign.id);
    setOptimizeResult(null);
    try {
      const res = await optimizeCampaign(campaign.id);
      setOptimizeResult({ name: campaign.name, suggestions: res.suggestions });
    } catch (e) {
      setOptimizeResult({ name: campaign.name, suggestions: "Error: " + e.message });
    } finally {
      setOptimizing(null);
    }
  };

  // Filter account dropdown to match selected platform
  const filteredAccounts = platform
    ? accounts.filter(a => a.platform === platform)
    : accounts;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Campaigns</div>
          <div className="page-subtitle">
            {campaigns.length} campaigns · {lastSynced ? `synced ${new Date(lastSynced).toLocaleTimeString()}` : "pending"}
          </div>
        </div>
        <div className="filter-row">
          <select className="select-input" value={platform} onChange={(e) => { setPlatform(e.target.value); setAccountId(""); }}>
            <option value="">All Platforms</option>
            <option value="google">Google Ads</option>
            <option value="meta">Meta Ads</option>
          </select>
          <select className="select-input" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">All Accounts</option>
            {filteredAccounts.map(a => (
              <option key={`${a.platform}-${a.id}`} value={String(a.id)}>
                [{a.platform === "google" ? "G" : "M"}] {a.name}
              </option>
            ))}
          </select>
          <select className="select-input" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="LAST_7_DAYS">Last 7 days</option>
            <option value="LAST_30_DAYS">Last 30 days</option>
            <option value="THIS_MONTH">This month</option>
            <option value="LAST_MONTH">Last month</option>
          </select>
          <button className="btn btn-ghost" onClick={sync}>↻ Sync</button>
        </div>
      </div>

      <div className="page-body">
        {error && (
          <div className="error-state">
            <h3>Failed to load campaigns</h3>
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="spinner" />
        ) : (
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  {[
                    { key: "name",        label: "Campaign" },
                    { key: "accountName", label: "Account" },
                    { key: "platform",    label: "Platform" },
                    { key: "spend",       label: "Spend" },
                    { key: "dailyBudget", label: "Budget/day" },
                    { key: "revenue",     label: "Revenue" },
                    { key: "roas",        label: "ROAS" },
                    { key: "clicks",      label: "Clicks" },
                    { key: "conversions", label: "Conv." },
                    { key: "convRate",    label: "Conv. Rate" },
                    { key: "ctr",         label: "CTR" },
                    { key: "avgCpc",      label: "Avg CPC" },
                    { key: "status",      label: "Status" },
                  ].map(col => (
                    <th key={col.key} onClick={() => requestSort(col.key)} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                      {col.label} {sortKey === col.key ? (sortDir === "asc" ? "▲" : "▼") : <span style={{ opacity: 0.3 }}>⇅</span>}
                    </th>
                  ))}
                  <th>AI Tips</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={14} style={{ textAlign: "center", color: "var(--text3)", padding: "40px 0" }}>
                      No campaigns — click Sync to pull live data from Google & Meta
                    </td>
                  </tr>
                ) : (
                  campaigns.map((c) => (
                    <tr key={c.id}>
                      <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</td>
                      <td style={{ fontSize: 12, color: "var(--text3)" }}>{c.accountName || "—"}</td>
                      <td><span className={`badge badge-${c.platform}`}>{c.platform}</span></td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>₹{Number(c.spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
                        {c.dailyBudget > 0
                          ? `₹${Number(c.dailyBudget).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                          : c.lifetimeBudget > 0
                          ? `₹${Number(c.lifetimeBudget).toLocaleString(undefined, { maximumFractionDigits: 0 })} (LT)`
                          : "—"}
                      </td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>₹{Number(c.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td style={{ fontFamily: "var(--mono)", color: roasColor(c.roas) }}>{Number(c.roas || 0).toFixed(2)}×</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.clicks || 0).toLocaleString()}</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.conversions || 0).toFixed(1)}</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.convRate || 0).toFixed(2)}%</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{Number(c.ctr || 0).toFixed(2)}%</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>₹{Number(c.avgCpc || 0).toFixed(2)}</td>
                      <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                      <td>
                        <button
                          className="btn btn-ghost"
                          style={{ padding: "5px 12px", fontSize: 12 }}
                          onClick={() => handleOptimize(c)}
                          disabled={optimizing === c.id}
                        >
                          {optimizing === c.id ? "…" : "✦ Optimize"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {optimizeResult && (
          <div className="card" style={{ marginTop: 16, borderColor: "rgba(91,127,255,0.3)" }}>
            <div className="card-title" style={{ color: "var(--accent)" }}>
              ✦ Claude AI — {optimizeResult.name}
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text)", whiteSpace: "pre-wrap" }}>
              {optimizeResult.suggestions}
            </p>
            <button
              className="btn btn-ghost"
              style={{ marginTop: 12, fontSize: 12 }}
              onClick={() => setOptimizeResult(null)}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function roasColor(r) {
  if (r >= 4)  return "var(--green)";
  if (r >= 2)  return "var(--amber)";
  return "var(--red)";
}
